import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { addMonths, startOfMonth } from "date-fns";
import { customFieldsToPayload } from "components/customer/build-pdf/build-pdf-utils";
import { fetchCompanyHolidays, fetchHolidaySettings } from "features/company-holidays/companyHolidays.service";
import type { CompanyHoliday, HolidaySettings } from "features/company-holidays/companyHolidays.types";
import { leaveApiErrorKey } from "features/leave-calendar/leaveApiErrors";
import { dayKey } from "features/leave-calendar/leaveCalendarDates";
import {
  resolveLeaveTemplatesForType,
  type LeaveTemplateInfo,
} from "features/leave-calendar/resolveLeaveTemplateClient";
import {
  createLeaveRequest,
  type CreatedLeaveRequest,
  type CreateLeaveRequestBody,
} from "services/leave-requests.service";
import { post as signWithClick } from "services/clicktosign.service";
import { clickSignErrorKey } from "utils/click-sign-errors";
import {
  countWorkingDays,
  holidayDayKeys,
  isOffDay,
  PickedRange,
  rangeProblemKey,
  tapDay,
  timesProblemKey,
  yearsOfRange,
} from "./leave-range";
import { fieldErrors, fieldsWithValues, hasUnfillableRequiredField } from "./leave-template-fields";

type LeaveDuration = CreateLeaveRequestBody["duration"];

/** What the sheet is told once the document is signed. */
export interface SignedResult {
  leaveRequest: CreatedLeaveRequest;
  /** The server says the request still waits for the manager. */
  managerApprovalPending: boolean;
  /** An earlier try to sign failed: the server's plain "ok" then does not say whether the manager is asked. */
  afterFailedAttempt: boolean;
}

/**
 * Everything behind the leave request sheet: what the person has picked and typed, the company's
 * templates and off days, and the two requests (create the request, then sign its document). The
 * sheet only draws what this returns.
 */
export function useLeaveRequestFlow({
  companyId,
  onSigned,
}: {
  companyId: string;
  onSigned: (result: SignedResult) => void;
}) {
  const [leaveType, setLeaveType] = useState("vacation");
  const [picked, setPicked] = useState<PickedRange | null>(null);
  const [chosenDuration, setChosenDuration] = useState<LeaveDuration>("full_day");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [comment, setComment] = useState("");
  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(new Date()));

  // null while the company's templates for the type are being looked up.
  const [templates, setTemplates] = useState<LeaveTemplateInfo[] | null>(null);
  const [chosenTemplateId, setChosenTemplateId] = useState<string | null>(null);
  /** The template's own fields, typed so far: template id, then field label. Kept when the person switches template and back. */
  const [typedFields, setTypedFields] = useState<Record<string, Record<string, string>>>({});
  /** Set by the first try to send with a field wrong, so that the lines under the fields appear. */
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [settings, setSettings] = useState<HolidaySettings>({ saturdayOff: true, sundayOff: true });
  const [holidaysByYear, setHolidaysByYear] = useState<Record<number, CompanyHoliday[]>>({});
  const askedYears = useRef(new Set<number>());

  /** True while the request is being created or signed. */
  const [isBusy, setIsBusy] = useState(false);
  /** Set once the request exists, whatever happens to its signature. */
  const [created, setCreated] = useState<CreatedLeaveRequest | null>(null);
  const signFailedBefore = useRef(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTemplates(null);
    resolveLeaveTemplatesForType(companyId, leaveType)
      .then((list) => !cancelled && setTemplates(list))
      // A failed lookup reads as "no template": the request cannot be sent either way.
      .catch(() => !cancelled && setTemplates([]));
    return () => {
      cancelled = true;
    };
  }, [companyId, leaveType]);

  useEffect(() => {
    // A failed request leaves the default (both weekend days off); the server still refuses an off day.
    fetchHolidaySettings(companyId)
      .then(setSettings)
      .catch(() => undefined);
  }, [companyId]);

  const visibleYear = visibleMonth.getFullYear();
  useEffect(() => {
    // The year on screen and the one before it (a company break is stored under the year it starts in), the
    // next one (a range that starts in December runs into it) and the years of the picked range.
    const rangeYears = picked ? yearsOfRange(picked) : [];
    const yearBeforeRange = rangeYears.length > 0 ? [rangeYears[0] - 1] : [];
    for (const year of [visibleYear - 1, visibleYear, visibleYear + 1, ...yearBeforeRange, ...rangeYears]) {
      if (askedYears.current.has(year)) continue;
      askedYears.current.add(year);
      fetchCompanyHolidays(companyId, year)
        .then((list) => setHolidaysByYear((byYear) => ({ ...byYear, [year]: list })))
        // A failed year is forgotten, so the next change of month or days asks for it again.
        .catch(() => askedYears.current.delete(year));
    }
  }, [companyId, visibleYear, picked]);

  const holidayDays = useMemo(() => holidayDayKeys(Object.values(holidaysByYear).flat()), [holidaysByYear]);
  const isOff = (day: string) => isOffDay(day, settings, holidayDays);
  const todayKey = dayKey(new Date());
  /** Past days and off days can be neither the first nor the last day (a range may still run across off days). */
  const isDayDisabled = (day: string) => day < todayKey || isOff(day);

  // The duration only applies to a single day; a longer range is always whole days.
  const isOneDay = picked !== null && picked.start === picked.end;
  const duration: LeaveDuration = isOneDay ? chosenDuration : "full_day";
  // The first and the last day are checked again here: they may have been picked before the settings or holidays arrived.
  const rangeProblem = picked ? rangeProblemKey(picked, isOff) : null;
  const problemKey = rangeProblem ?? timesProblemKey(duration, startTime, endTime);

  const templateList = templates ?? [];
  const template = templateList.find((candidate) => candidate.id === chosenTemplateId) ?? templateList[0] ?? null;
  const typedForTemplate = (template && typedFields[template.id]) || {};
  const customFields = fieldsWithValues(template?.customFields ?? [], typedForTemplate);
  const hasUnfillableField = hasUnfillableRequiredField(customFields);
  const errorsOfFields = template && picked ? fieldErrors(template.id, picked.start, customFields) : {};

  const canSubmit = !isBusy && !created && picked !== null && template !== null && !problemKey && !hasUnfillableField;
  // The working days depend on the holidays of the years the range touches; until those are known, no count is claimed.
  // A break stored under the year before the first day (28 Dec – 6 Jan) counts too.
  const areHolidaysKnown =
    picked !== null &&
    [yearsOfRange(picked)[0] - 1, ...yearsOfRange(picked)].every((year) => year in holidaysByYear);

  const submit = async () => {
    if (!canSubmit || !picked || !template) return;
    if (Object.keys(errorsOfFields).length > 0) {
      setShowFieldErrors(true);
      return;
    }
    setIsBusy(true);
    setErrorKey(null);
    const body: CreateLeaveRequestBody = {
      leaveType,
      templateId: template.id,
      duration,
      startDate: picked.start,
      endDate: picked.end,
      ...(duration === "full_day" ? {} : { startTime, endTime }),
      comment: comment.trim() || undefined,
      customFields: customFieldsToPayload(customFields),
    };
    try {
      const { leaveRequest } = await createLeaveRequest(companyId, body);
      setCreated(leaveRequest);
    } catch (error) {
      // The server names the reason in `error`; the comment check names it in `code`.
      const answer = axios.isAxiosError(error) ? error.response?.data : undefined;
      setErrorKey(leaveApiErrorKey(answer?.error ?? answer?.code));
    } finally {
      setIsBusy(false);
    }
  };

  const sign = async () => {
    if (!created || isBusy) return;
    setIsBusy(true);
    setErrorKey(null);
    try {
      const answer = await signWithClick(companyId, { fileId: created.fileId });
      // "sent signature request to the manager", "manager signature pending" or "manager approval pending".
      const managerApprovalPending = Boolean(answer?.message);
      onSigned({ leaveRequest: created, managerApprovalPending, afterFailedAttempt: signFailedBefore.current });
    } catch (error) {
      signFailedBefore.current = true;
      setErrorKey(clickSignErrorKey(error, "leave_sheet_error_sign"));
    } finally {
      setIsBusy(false);
    }
  };

  return {
    leaveType,
    chooseType: setLeaveType,
    hasNoTemplate: templates !== null && templates.length === 0,
    /** The templates of the type, and the one in use: the person chooses when there are several. */
    templates: templateList,
    template,
    chooseTemplate: (id: string) => {
      setChosenTemplateId(id);
      // A newly chosen template's fields are not marked wrong before the person tries to send them.
      setShowFieldErrors(false);
    },
    templateFields: {
      list: customFields,
      /** Empty until the person tried to send the request. */
      errors: showFieldErrors ? errorsOfFields : {},
      hasUnfillableField,
      setValue: (label: string, value: string) => {
        if (!template) return;
        setTypedFields((all) => ({ ...all, [template.id]: { ...all[template.id], [label]: value } }));
      },
    },
    month: visibleMonth,
    // Nothing before this month can be picked.
    canGoToPreviousMonth: visibleMonth > startOfMonth(new Date()),
    changeMonth: (months: number) => setVisibleMonth((month) => addMonths(month, months)),
    picked,
    pickDay: (day: string) => setPicked((current) => tapDay(current, day)),
    isDayDisabled,
    /** null while the holidays of the range's years are not known. */
    workingDays: picked && areHolidaysKnown ? countWorkingDays(picked.start, picked.end, isOff) : null,
    isOneDay,
    duration,
    chooseDuration: setChosenDuration,
    times: { start: startTime, end: endTime, setStart: setStartTime, setEnd: setEndTime },
    comment,
    setComment,
    problemKey,
    canSubmit,
    submit,
    created,
    sign,
    isBusy,
    errorKey,
  };
}
